<template>
  <fieldset :class="$style.loginFieldset">
    <label :class="$style.loginTextFieldLabel">
      {{ label }}
    </label>
    <div :class="$style.inputContainer">
      <input
        v-focus
        v-validate="validationRules"
        :autocomplete="autocomplete ? 'on' : 'off'"
        :class="$style.loginInput"
        :data-vv-name="vvName"
        :maxlength="maxlength"
        :name="name"
        :placeholder="placeholder"
        :required="required"
        :type="type"
        :value="value"
        :disabled="!isEditable"
        @focus="handleFocus"
        @input="handleInput"
        @keydown.enter.prevent="submitOnEnter"
      >

      <transition
        :enterClass="$style.fadeRightEnter"
        :enterActiveClass="$style.fadeRightEnterActive"
        :leaveActiveClass="$style.fadeRightLeaveActive"
        :leaveToClass="$style.fadeRightLeaveTo"
      >
        <i
          :class="['jc-actions-alert', $style.invalidIcon]"
          v-if="errors.has(vvName)"
        />
      </transition>

      <transition
        :enterClass="$style.fadeUpEnter"
        :enterActiveClass="$style.fadeUpEnterActive"
        :leaveActiveClass="$style.fadeUpLeaveActive"
        :leaveToClass="$style.fadeUpLeaveTo"
      >
        <div
          :class="$style.errorDisplay"
          :key="errors.first(vvName)"
          v-if="errors.has(vvName)"
        >
          <i :class="$style.errorDisplayPointer" />
          {{ errors.first(this.vvName) }}
        </div>
      </transition>
    </div>
  </fieldset>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'LoginTextField',

  inject: ['$validator'],

  $_veeValidate: {
    events: '',
  },

  props: {
    autocomplete: {
      type: Boolean,
      default: true,
    },

    autoFocus: {
      type: Boolean,
      default: false,
    },

    isEditable: {
      type: Boolean,
      default: true,
    },

    label: {
      type: String,
      default: '',
    },

    maxlength: {
      type: Number,
      default: null,
    },

    name: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: '',
    },

    required: {
      type: Boolean,
      default: false,
    },

    rules: {
      type: Object,
      default: () => {},
    },

    submitOnEnter: {
      type: Function,
      default: () => {},
    },

    type: {
      type: String,
      default: 'text',
    },

    value: {
      type: String,
      required: true,
    },

    vvName: {
      type: String,
      required: true,
    },
  },

  methods: {
    handleFocus() {
      this.$emit('focus');
    },
    handleInput(e) {
      this.$emit('input', e.target.value);
    },

    validateTextField(newValue, oldValue) {
      const field = this.$validator.fields.find(this.vvName);
      const { validated, invalid } = field.flags;

      // if the field has been validated and needs to be corrected, validate on input
      if (validated && (invalid || !oldValue)) {
        return this.$validator.validate(this.vvName, newValue)
          .then((result) => {
            /*
             * Manually set the value of `invalid` here due to a vee-validate
             * bug not updating `invalid` correctly for inputs using "required"
             */
            field.flags.invalid = !result;
            return field.flags.invalid;
          });
      }

      return invalid;
    },


  },

  computed: {
    validationRules() {
      const rules = {
        required: this.required,
        email: this.type === 'email',
      };
      if (this.rules) {
        _.extend(rules, this.rules);
      }
      return rules;
    },
  },

  directives: {
    focus: {
      inserted(el, binding, vnode) {
        if (vnode.context.autoFocus) {
          el.focus();
        }
      },
    },
  },


  watch: {
    /*
      We refrain from using Vee Validate events here because
      we need to make sure that the input has been validated first
      before we throw an error.
     */
    value(newValue, oldValue) {
      this.validateTextField(newValue, oldValue);
    },
  },
};
</script>

<style module>
@import '../css/login-components.css';
@import '../css/inputs.css';

@value (grey96) from 'css/colors.css';

input:disabled {
  background-color: grey96;
}
</style>
